import axios from 'axios'

// Use relative path in production, absolute in development
const API_BASE_URL = import.meta.env.VITE_API_URL || 
  (import.meta.env.DEV ? 'http://localhost:5000/api' : '/api')

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
})

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`)
    return config
  },
  (error) => {
    console.error('API Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message)
    
    // Handle specific error cases
    if (error.response) {
      // Server responded with error status
      const status = error.response.status
      const message = error.response.data?.error || error.response.data?.message || 'En fejl opstod'
      
      if (status === 404) {
        throw new Error('Ressource ikke fundet')
      } else if (status === 500) {
        throw new Error('Server fejl - prøv igen senere')
      } else {
        throw new Error(message)
      }
    } else if (error.request) {
      // Request made but no response
      throw new Error('Ingen forbindelse til serveren - tjek din internetforbindelse')
    } else {
      // Error setting up request
      throw new Error('Fejl ved oprettelse af forespørgsel')
    }
  }
)

export const getProducts = async (params = {}) => {
  try {
    const response = await api.get('/products', { params })
    return response.data
  } catch (error) {
    console.error('Error fetching products:', error)
    throw error
  }
}

export const getProduct = async (id) => {
  try {
    const response = await api.get(`/products/${id}`)
    return response.data
  } catch (error) {
    console.error(`Error fetching product ${id}:`, error)
    throw error
  }
}

export const getCategories = async () => {
  try {
    const response = await api.get('/products/categories')
    return response.data
  } catch (error) {
    console.error('Error fetching categories:', error)
    throw error
  }
}

export const getBrands = async (category = null) => {
  try {
    const params = category ? { category } : {}
    const response = await api.get('/products/brands', { params })
    return response.data
  } catch (error) {
    console.error('Error fetching brands:', error)
    throw error
  }
}

export const getSpecValues = async (category, spec) => {
  try {
    const response = await api.get('/products/spec-values', { params: { category, spec } })
    return response.data || []
  } catch (error) {
    console.error('Error fetching spec values:', error)
    return []
  }
}

export const getYears = async (category = null) => {
  try {
    const params = category ? { category } : {}
    const response = await api.get('/products/years', { params })
    return response.data || []
  } catch (error) {
    console.error('Error fetching years:', error)
    return []
  }
}

export const recommendProducts = async (data) => {
  try {
    const response = await api.post('/comparison/recommend', data)
    return response.data
  } catch (error) {
    console.error('Error getting recommendations:', error)
    throw error
  }
}

export const getRelatedProducts = async (productId) => {
  try {
    const response = await api.get(`/products/${productId}/related`)
    return response.data
  } catch (error) {
    console.error(`Error fetching related products for ${productId}:`, error)
    throw error
  }
}

export const getFeed = async (limit = 8) => {
  try {
    const response = await api.get('/feed', { params: { limit } })
    return response.data
  } catch (error) {
    console.error('Error fetching feed:', error)
    throw error
  }
}

export const compareProducts = async (productIds) => {
  try {
    const response = await api.post('/comparison/compare', { product_ids: productIds })
    return response.data
  } catch (error) {
    console.error('Error comparing products:', error)
    throw error
  }
}

export default api

